import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, isDevMode } from '@angular/core';
import { GetFiltersForMyCampaignsResponse,AnalyzeUrlsRequest, AnalyzeUrlsResponse, DeleteCampaignRequest, DeleteCampaignResponse, GenerateCampaignsFromAIResponse, GetCampaignsEventsRequest, GetCampaignsEventsResponse, GetCampaignsIdeasRequest, GetCampaignsIdeasResponse, GetMyCampaignsRequest, GetMyCampaignsResponse, IsCampaignInProgressResponse, RenameCampaignRequest, RenameCampaignResponse, UpdateCampaignStatusRequest, UpdateCampaignStatusResponse, UpdateProjectStatusRequest, UpdateProjectStatusResponse, UploadInstDocsRequest, UploadInstDocsResponse, UploadKeywordDocumentResponse, UploadKeywordResponse } from '../../_shared/models/campaigns';
import { BehaviorSubject, Observable, catchError, delay, expand, of, takeWhile, throwError } from 'rxjs';
import { GenerateCampaignArticlesForCustomTemplateRequest, GenerateCampaignArticlesRequest, GenerateCampaignArticlesResponse, RetryGenerateCampaignArticlesForCustomTemplateRequest, RetryGenerateCampaignArticlesForCustomTemplateResponse, WebpageKeywordTemplateMappingRequest, WebpageKeywordTemplateMappingResponse } from 'src/app/_shared/models/GenerateCampaignArticlesRequest';


@Injectable({
  providedIn: 'root'
})
export class CampaignService {

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  constructor(
    private http: HttpClient,
    @Inject('BASE_URL') private baseUrl: string
  ) {

  }

  public GetFiltersForMyCampaigns<T>(profileId: number): Observable<GetFiltersForMyCampaignsResponse> {
    return this.http.post<GetFiltersForMyCampaignsResponse>(
      `${this.baseUrl}api/Campaign/GetFiltersForMyCampaigns?profileId=${profileId}`,
      null,
      { headers: this.headers }
    );
  }

  public GetMyCampaigns<T>(request: GetMyCampaignsRequest): Observable<GetMyCampaignsResponse> {
    return this.http.post<GetMyCampaignsResponse>(
      `${this.baseUrl}api/Campaign/GetMyCampaigns`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public GetCampaignsEvents<T>(request: GetCampaignsEventsRequest): Observable<GetCampaignsEventsResponse> {
    return this.http.post<GetCampaignsEventsResponse>(
      `${this.baseUrl}api/Campaign/GetCampaignsEvents`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public GetCampaignsIdeas<T>(request: GetCampaignsIdeasRequest): Observable<GetCampaignsIdeasResponse> {
    return this.http.post<GetCampaignsIdeasResponse>(
      `${this.baseUrl}api/Campaign/GetCampaignsIdeas`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public UpdateCampaignStatus<T>(request: UpdateCampaignStatusRequest): Observable<UpdateCampaignStatusResponse> {
    return this.http.post<UpdateCampaignStatusResponse>(
      `${this.baseUrl}api/Campaign/UpdateCampaignStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public DeleteCampaign<T>(request: DeleteCampaignRequest): Observable<DeleteCampaignResponse> {
    return this.http.post<DeleteCampaignResponse>(
      `${this.baseUrl}api/Campaign/DeleteCampaign`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public ResetAndGenerateCampaigns<T>(): Observable<GenerateCampaignsFromAIResponse> {
    return this.http.post<GenerateCampaignsFromAIResponse>(
      `${this.baseUrl}api/Campaign/ResetAndGenerateCampaigns`,
      { headers: this.headers }
    );
  }

  public RenameCampaign<T>(request: RenameCampaignRequest): Observable<RenameCampaignResponse> {
    return this.http.post<RenameCampaignResponse>(
      `${this.baseUrl}api/Campaign/RenameCampaign`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public AnalyzeUrls<T>(request: AnalyzeUrlsRequest): Observable<AnalyzeUrlsResponse> {
    return this.http.post<AnalyzeUrlsResponse>(
      `${this.baseUrl}api/Campaign/analyzeUrls`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public uploadKeywords<T>(file: File): Observable<UploadKeywordResponse> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<UploadKeywordResponse>(
      `${this.baseUrl}api/Campaign/uploadKeywords`, formData,
      { reportProgress: true, responseType: 'json' }
    );
  }

  public getKeywordDocumentSample(businessId: number): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}api/Campaign/keyword-document-sample`,
      { params: { businessId: businessId.toString() }, responseType: 'blob' }
    );
  }

  public uploadKeywordDocument(file: File): Observable<UploadKeywordDocumentResponse> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<UploadKeywordDocumentResponse>(
      `${this.baseUrl}api/Campaign/keyword-document-upload`,
      formData,
      { reportProgress: true, responseType: 'json' }
    ).pipe(
      catchError((err) => of(err?.error as UploadKeywordDocumentResponse))
    );
  }

  public uploadInstructionDocuments<T>(request: UploadInstDocsRequest): Observable<UploadInstDocsResponse> {

    const formData: FormData = new FormData();    
    //append multiple files
    // Append files with metadata
    request.files.forEach((f, index) => {
      // File
      formData.append(`Files[${index}].File`, f.file, f.file.name);
      // DocumentType
      formData.append(`Files[${index}].DocumentType`, f.documentType);
      // Document Name (for other type)
      formData.append(`Files[${index}].DocumentDescription`, f.documentDescription);
    });

    return this.http.post<UploadInstDocsResponse>(
      `${this.baseUrl}api/Campaign/uploadInstructionDocuments`,
      formData,
      {
        reportProgress: true,
        responseType: 'json' }
      );
  }

  isCampaignInProgress(profileId: number): Observable<IsCampaignInProgressResponse> {
    return this.http.get<IsCampaignInProgressResponse>(
      `${this.baseUrl}api/Campaign/isCampaignInProgress/${profileId}`
    );
  }

  public GenerateCampaignArticlesForCustomTemplate<T>(request: GenerateCampaignArticlesRequest): Observable<GenerateCampaignArticlesResponse> {
    return this.http.post<GenerateCampaignArticlesResponse>(
      `${this.baseUrl}api/Campaign/GenerateCampaignArticlesForCustomTemplate`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  public WebpageKeywordTemplateMapping(request: WebpageKeywordTemplateMappingRequest): Observable<WebpageKeywordTemplateMappingResponse> {
      return this.http.post<WebpageKeywordTemplateMappingResponse>(
        `${this.baseUrl}api/Campaign/WebpageKeywordTemplateMapping`,
        request,
        { headers: this.headers }
      );
  }

  public GenerateCampaignArticlesForCustomTemplateNew<T>(request: GenerateCampaignArticlesForCustomTemplateRequest): Observable<GenerateCampaignArticlesResponse> {

    const formData: FormData = new FormData();
    formData.append('CampaignTopic', request.CampaignTopic || '');
    formData.append('CampaignGoal', request.CampaignGoal || '');
    formData.append('BrandId', request.brandId || '');
    formData.append('ProfileId', request.profileId?.toString() || '');
    formData.append('BusinessId', request.businessId?.toString() || '');
    formData.append('ProfileName', request.profileName || '');
    formData.append('IsIndividualUrls', request.isIndividualUrls?.toString() || 'false');
    formData.append('ContentBrief', request.contentBrief || '');
    formData.append('Tone', request.tone || '');
    formData.append(
      'LanguageDetail',
      JSON.stringify(request.languageDetail)
    );

    formData.append(
      'Language',
      request.language ?? ''
    );


    formData.append(
      'BrandVoiceId',
      String(request.brandVoiceId ?? 0)
    );
      
    formData.append(
      'TargetAudienceId',
      String(request.targetAudienceId ?? 0)
    );

    (request.Templates || []).forEach((template, index) => {
      formData.append(`Templates[${index}].Title`, template.title);
      formData.append(`Templates[${index}].CustomTemplateId`, template.CustomTemplateId?.toString() || '');
      formData.append(`Templates[${index}].PageTitle`, template.PageTitle || '');
      formData.append(`Templates[${index}].TemplateId`, template.templateId?.toString() || '');

      (template.Keywords || []).forEach((k, kIndex) => {
        formData.append(`Templates[${index}].Keywords[${kIndex}]`, k);
      });
    });

    (request.pages || []).forEach((page, index) => {
      formData.append(`Pages[${index}].Title`, page.title);
      formData.append(`Pages[${index}].TemplateId`, page.templateId);
      formData.append(`Pages[${index}].PageUrl`, page.pageUrl);

      (page.keywords || []).forEach((k, kIndex) => {
        formData.append(`Pages[${index}].Keywords[${kIndex}]`, k);
      });
    });

    if (request.topicIdea) {

      formData.append('TopicIdea.PillarIdea', request.topicIdea.pillarIdea || '');

      (request.topicIdea.keywords || []).forEach((k, index) => {
        formData.append(`TopicIdea.Keywords[${index}]`, k);
      });

    }
    if (request.highlights) {
      Object.keys(request.highlights).forEach(key => {

        const values = request.highlights[key];

        values.forEach((value: string, index: number) => {
          formData.append(`Highlights[${key}][${index}]`, value);
        });

      });
    }

    (request.urls || []).forEach((url, index) => {
      formData.append(`Urls[${index}]`, url);
    });

    (request.keywords || []).forEach((keyword, index) => {
      formData.append(`Keywords[${index}]`, keyword);
    });

    request.files.forEach((f, index) => {
      // File
      formData.append(`Files[${index}].File`, f.file, f.file.name);
      // DocumentType
      formData.append(`Files[${index}].DocumentType`, f.documentType);
      // Document Name (for other type)
      formData.append(`Files[${index}].DocumentDescription`, f.documentDescription);
    });

    if (request.keywordDocumentData) {
      formData.append('KeywordDocumentDataJson', JSON.stringify(request.keywordDocumentData));
    }

    if (request.keywordDocumentFile) {
      formData.append('KeywordDocumentFile', request.keywordDocumentFile, request.keywordDocumentFile.name);
    }

    return this.http.post<GenerateCampaignArticlesResponse>(
      `${this.baseUrl}api/Campaign/GenerateCampaignArticlesForCustomTemplate`,
      formData
    );
  }

  public RetryGenerateCampaignArticlesForCustomTemplate<T>(request: RetryGenerateCampaignArticlesForCustomTemplateRequest): Observable<RetryGenerateCampaignArticlesForCustomTemplateResponse> {
    return this.http.post<RetryGenerateCampaignArticlesForCustomTemplateResponse>(
      `${this.baseUrl}api/Campaign/RetryGenerateCampaignArticlesForCustomTemplate`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }

  private reloadCampaignSubject = new BehaviorSubject<boolean>(false);
  reloadCampaign$ = this.reloadCampaignSubject.asObservable();
  
  triggerReload() {
    this.reloadCampaignSubject.next(true);
  }

    public UpdateProjectStatus(request: UpdateProjectStatusRequest): Observable<UpdateProjectStatusResponse> {
    return this.http.post<UpdateProjectStatusResponse>(
      `${this.baseUrl}api/Campaign/UpdateProjectStatus`,
      JSON.stringify(request),
      { headers: this.headers }
    );
  }
}
